import { CountryCode, PhoneNumber } from 'libphonenumber-js';
import parsePhoneNumberFromString from 'libphonenumber-js/max';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { z } from 'zod';
import { useSearchParams } from 'react-router-dom';
import { UploadFile } from 'antd';

export type AllowedFileTypes =
  | 'image/jpeg'
  | 'image/png'
  | 'image/jpg'
  | 'image/gif'
  | 'video/mp4'
  | 'video/webm'
  | 'application/pdf'
  | 'application/msword'
  | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export interface BeDocument {
  id: string;
  type: string | null;
  name: string;
  blobName: string;
  blobFileUrl: string;
  blobSize: number;
}

const Helpers = {
  isFileSizeAllowed: (maxFileSizeMB: number, sizeInBytes: number) => {
    return sizeInBytes <= maxFileSizeMB * 1024 * 1024;
  },
};

export const isArabic = (str: string) => /^[\u0600-\u06FF\s]+$/.test(str);
export const isEnglish = (str: string) => /^[a-zA-Z\s]+$/.test(str);

export const youtubeRegex =
  /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:\S+)?$/;

export const urlRegex =
  /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(:\d+)?(\/[\w-./?%&=]*)?$/;

export const useSharedValidations = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [searchParams] = useSearchParams();
  const [errorMessageEmail, setErrorMessage] = useState('');

  // Dummy mock for auth userType since we are in admin
  const userType = 'business-seller';

  const isValidEmail = async (email: string) => {
    try {
      const response = await fetch(
        `${process.env.VITE_GATEWAY_API_BASEURL || 'https://dev.api.yadsupplier.yad-plus.com'}/auth-service/v1/users/validate-email`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'accept-language': locale,
          },
          body: JSON.stringify({
            email,
            userType,
          }),
        }
      );
      if (!response.ok) {
        const errorPayload = await response?.json();
        throw errorPayload;
      }
      return true;
    } catch (err) {
      console.log('err : ', err);
      //@ts-expect-error error obj
      setErrorMessage(err?.message);
      return false;
    }
  };

  const required_error = t('components.validations.requiredField', 'Field is required');
  const zodRequiredStringValidate = z
    .string({
      required_error,
      invalid_type_error: t('components.validations.requiredField'),
    })
    .min(1, { message: required_error });

  const stateValidation = z.string({
    required_error: t('components.validations.requiredField'),
  });

  const streetValidation = z.preprocess(
    (val) => {
      if (val == null) return val;
      if (typeof val !== 'string') return val;
      const trimmed = val.trim();
      return trimmed === '' ? undefined : trimmed;
    },
    z
      .string()
      .min(3, {
        message: t('components.validations.minLength', { value: 3 }),
      })
      .max(20, {
        message: t('components.validations.maxLength', { value: 20 }),
      })
      .optional()
      .nullable()
  );

  const phoneNumberValidation = z
    .object(
      {
        code: zodRequiredStringValidate,
        number: zodRequiredStringValidate,
        countryCode: z.string({
          required_error: t('components.validations.MOBILE_INVALID'),
          invalid_type_error: t('components.validations.MOBILE_INVALID'),
        }),
      },
      { required_error }
    )
    .refine(
      (value) => {
        try {
          const phoneNumber: PhoneNumber | undefined =
            parsePhoneNumberFromString(
              `${value.number}`,
              value.countryCode as CountryCode
            );
          if (!phoneNumber || !phoneNumber.isValid()) {
            return false;
          }
          const type = phoneNumber.getType();
          return type?.includes('MOBILE');
        } catch (error) {
          console.error('Phone validation error:', error);
          return false;
        }
      },
      { message: t('components.validations.MOBILE_INVALID') }
    );
    
  const acceptTermsValidation = z
    .boolean({ required_error })
    .array()
    .length(1, { message: t('components.validations.accept-terms-error') });
    
  const isEmailValid = z
    .string({ required_error })
    .email({ message: t('components.validations.invalidEmail') });

  const emailValidation = z
    .string({ required_error })
    .email({ message: t('components.validations.invalidEmail') })
    .refine(
      async (val) => {
        const token = searchParams.get('token');
        if (token) {
          return true;
        }
        const { success } = isEmailValid?.safeParse(val);
        if (success) {
          const isValid = await isValidEmail(val);
          return isValid;
        }
        return true;
      },
      { message: errorMessageEmail }
    );

  const UploadFileSchema = z.object({
    uid: z.string(),
    size: z.number().optional(),
    name: z.string(),
    fileName: z.string().optional(),
    lastModified: z.number().optional(),
    lastModifiedDate: z.date().optional(),
    url: z.string().url().optional(),
    status: z.string().optional(),
    percent: z.number().optional(),
    thumbUrl: z.string().url().optional(),
    crossOrigin: z.string().optional(),
    originFileObj: z.any().optional(),
    response: z.any().optional(),
    error: z.any().optional(),
    linkProps: z.any().optional(),
    type: z.string().optional(),
    xhr: z.any().optional(),
    preview: z.string().optional(),
  });

  const namesValidation = z
    .object({
      firstName: zodRequiredStringValidate
        .trim()
        .min(2, {
          message: t('components.validations.minLength', { value: 2 }),
        })
        .max(20, {
          message: t('components.validations.maxLength', { value: 20 }),
        }),
      lastName: zodRequiredStringValidate
        .trim()
        .min(2, {
          message: t('components.validations.minLength', { value: 2 }),
        })
        .max(20, {
          message: t('components.validations.maxLength', { value: 20 }),
        }),
    })
    .superRefine((data, ctx) => {
      if (!data.firstName.trim() || !data.lastName.trim()) return;

      const firstNameIsArabic = isArabic(data.firstName);
      const firstNameIsEnglish = isEnglish(data.firstName);
      const lastNameIsArabic = isArabic(data.lastName);
      const lastNameIsEnglish = isEnglish(data.lastName);

      const valid =
        (firstNameIsArabic && lastNameIsArabic) ||
        (firstNameIsEnglish && lastNameIsEnglish);

      if (!valid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('components.validations.namesMustBeSame'),
          path: ['firstName'],
        });

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('components.validations.namesMustBeSame'),
          path: ['lastName'],
        });
      }
    });

  const introUrlFielValidation = z
    .string()
    .superRefine((val, ctx) => {
      const trimmed = val?.trim();

      if (!!trimmed && !youtubeRegex.test(trimmed)) {
        ctx.addIssue({
          code: 'custom',
          message: t('components.invalidURL'),
        });
      }
    })
    .optional()
    .nullable();

  const websiteValidation = z
    .string()
    .superRefine((val, ctx) => {
      const trimmed = val?.trim();

      if (!!trimmed && !urlRegex.test(trimmed)) {
        ctx.addIssue({
          code: 'custom',
          message: t('components.invalidURL'),
        });
      }
    })
    .optional()
    .nullable();

  const createDescriptionSchemaFun = (
    min: number,
    max: number,
    optional?: boolean
  ) => {
    const schema = z
      .string({ required_error, invalid_type_error: required_error })
      .regex(/^(?!\s+$).*/, { message: required_error })
      .trim()
      .refine(
        (value) => {
          if (optional && value === '') return true;
          if (!optional && value === '') return false;
          return true;
        },
        {
          message: required_error,
        }
      )
      .refine(
        (value) => {
          if ((optional && value === '') || !value) return true;
          return value.length >= min;
        },
        {
          message: t('components.validations.minLength', { value: min }),
        }
      )
      .refine(
        (value) => {
          if ((optional && value === '') || !value) return true;
          return value.length <= max;
        },
        {
          message: t('components.validations.maxLength', { value: max }),
        }
      )
      .refine(
        (value) => {
          if (!optional && value === '') return false;
          return true;
        },
        {
          message: required_error,
        }
      );

    if (optional) return schema.optional();
    return schema;
  };

  const beDocumentObject = z.object({
    id: z.string(),
    type: z.string().nullable(),
    name: z.string(),
    blobName: z.string(),
    blobFileUrl: z.string().url(),
    blobSize: z.number(),
  });

  const documentsValidationFun = (
    min: number,
    max: number,
    acceptedFileTypes: AllowedFileTypes[],
    maxFileSizeMB: number,
    optional?: boolean
  ) => {
    const schema = z
      .any()
      .refine(
        (files) => {
          if (!optional && files?.length < 1) return false;
          return true;
        },
        {
          message: t('components.upload_requirement'),
        }
      )
      .refine(
        (files) => {
          if (optional && !!files === false) return true;
          return files?.every(
            (file: BeDocument | UploadFile) =>
              acceptedFileTypes.includes(file?.type as AllowedFileTypes) ||
              ('id' in file && typeof file?.name === 'string')
          );
        },
        {
          message: t('components.invalid_file_type'),
        }
      )
      .refine(
        (files) => {
          if (optional && !!files === false) return true;

          return files?.every((file: UploadFile) => {
            return Helpers.isFileSizeAllowed(
              maxFileSizeMB,
              file.size as number
            );
          });
        },
        {
          message: t('components.file_size_limit'),
        }
      )
      .refine((files) => (files?.length ?? 0) <= max, {
        message: t('components.validations.maxFielsLength', { num: max }),
      })
      .refine((files) => (files?.length ?? 0) >= min, {
        message: t('components.validations.minFielsLength', { num: min }),
      });

    return schema;
  };

  return {
    required_error,
    zodRequiredStringValidate,
    stateValidation,
    streetValidation,
    phoneNumberValidation,
    acceptTermsValidation,
    emailValidation,
    UploadFileSchema,
    namesValidation,
    introUrlFielValidation,
    websiteValidation,
    beDocumentObject,
    createDescriptionSchemaFun,
    documentsValidationFun,
  };
};
